import React from 'react'
import BigNumber from 'bignumber.js'
import Balance from 'components/Balance'
import { AutoColumn } from 'components/Layout/Column'
import { RowFixed } from 'components/Layout/Row'
import CurrencyLogo from 'components/Logo/CurrencyLogo'
import { TagComponent } from 'components/Tags'
import LocalBalance from 'components/LocalBalance'
import tokens from 'config/constants/tokens'
import { useTranslation } from 'contexts/Localization'
import { Card, useModal, Flex, Box, Skeleton } from 'vvs-uikit'
import { useUserVvsBar, useUserVvsBoost } from 'state/xVvs/hooks'
import { formatNumber, getBalanceAmount } from 'utils/formatBalance'
import { useTokenBalance } from 'state/tokens/useTokensUserBalance'
import { FetchStatus } from 'state/tokens/types'
import RoiCalculatorModal from 'components/RoiCalculatorModal'
import { BIG_ZERO } from 'utils/bigNumber'
import useTheme from 'hooks/useTheme'
import { usePriceVvsUsdc } from 'state/farms/hooks'
import useXvvsRate from 'views/xVvs/hooks/useXvvsRate'
import { tryParseAmount } from 'state/swap/hooks'
import usePastWeekApr from 'views/xVvs/hooks/usePastWeekApr'
import { StyledTextWithUrl } from 'components/Text/StyledTextWithUrl'
import { StyledCard, Body, FixedHeightRow, StyledText } from '../../styles'
import AprRow from './AprRow'
import StyledVvsBarCardHeader from '../StyledVvsBarCardHeader'
import ActionButtons from './ActionButtons'
import RecommendSection from './RecommendSection'

const BarCard: React.FC = () => {
  const { t } = useTranslation()
  const { theme } = useTheme()

  const { balance, fetchStatus } = useTokenBalance(tokens.xvvs.address)
  const { balance: vvsBalance } = useTokenBalance(tokens.vvs.address)
  const price = usePriceVvsUsdc()

  const apr = usePastWeekApr()

  const { userDataLoaded } = useUserVvsBar()

  const { boostData } = useUserVvsBoost()
  const rate = useXvvsRate()

  const totalLocked = boostData.stakes
    .filter((x) => x.active)
    .map((x) => x.amount)
    .reduce((a, b) => a.plus(b), BIG_ZERO)

  const totalXvvs = balance.plus(totalLocked)
  let totalValueInVvsSignificant = '0'
  let totalValueInUsd = new BigNumber(0)
  if (totalXvvs.gt(0)) {
    totalValueInVvsSignificant = tryParseAmount(
      getBalanceAmount(totalXvvs, tokens.xvvs.decimals).toString(),
      tokens.xvvs,
    )
      .multiply(rate ?? '1')
      .toSignificant(18)

    totalValueInUsd = price.times(totalValueInVvsSignificant)
  }

  const [openRoiModal] = useModal(
    <RoiCalculatorModal
      linkLabel={t('Get VVS')}
      stakingTokenBalance={vvsBalance}
      stakingTokenSymbol={tokens.vvs.symbol}
      stakingTokenPrice={price.toNumber()}
      earningTokenPrice={price.toNumber()}
      earningTokenSymbol={tokens.vvs.symbol}
      apr={apr}
      linkHref={`./swap?inputCurrency=${tokens.usdc.address}&outputCurrency=${tokens.vvs.address}`}
      tooltipText={t('“My Balance” here includes %assetSymbol% in your wallet.', { assetSymbol: tokens.vvs.symbol })}
    />,
  )

  return (
    <StyledCard>
      <StyledVvsBarCardHeader earningToken={tokens.xvvs} stakingToken={tokens.vvs} />
      <Body>
        <Box mb="16px">
          <AprRow apr={apr} text={t('LAST 7 DAYS APR')} onCalculatorClick={openRoiModal} />
        </Box>
        <Card p={0} background={theme.colors.invertedContrast}>
          <AutoColumn gap="2px" style={{ padding: '16px 24px 24px' }}>
            <FixedHeightRow mt={2}>
              <RowFixed>
                <CurrencyLogo size="16px" currency={tokens.vvs} />
                <StyledText ml="8px" small>
                  {t('Total Value in VVS')}
                </StyledText>
              </RowFixed>
              <RowFixed>
                <Balance
                  fontSize="18px"
                  bold
                  color="text"
                  value={parseFloat(totalValueInVvsSignificant)}
                  decimals={2}
                  ml="6px"
                />
              </RowFixed>
            </FixedHeightRow>
            <Flex justifyContent="end">
              <LocalBalance prefix="~ " color="textSubtle" fontSize="9.75px" value={totalValueInUsd} />
            </Flex>
            <FixedHeightRow mt={3}>
              <RowFixed>
                <CurrencyLogo size="16px" currency={tokens.xvvs} />
                <StyledText ml="8px" small>
                  {t('Total xVVS')}
                </StyledText>
              </RowFixed>
              <RowFixed>
                <Balance
                  fontSize="18px"
                  bold
                  color="text"
                  value={getBalanceAmount(totalXvvs, tokens.xvvs.decimals).toNumber()}
                  decimals={2}
                  ml="6px"
                />
              </RowFixed>
            </FixedHeightRow>

            <FixedHeightRow mt={2}>
              <StyledText small>{t('Wallet')}</StyledText>
              {fetchStatus !== FetchStatus.SUCCESS ? (
                <Skeleton height="22px" width="60px" />
              ) : (
                <Balance
                  fontSize="14px"
                  bold
                  color="text"
                  value={getBalanceAmount(balance, tokens.xvvs.decimals).toNumber()}
                  decimals={2}
                  ml="6px"
                />
              )}
            </FixedHeightRow>
            <FixedHeightRow>
              <StyledText small>{t('Vault')}</StyledText>
              {!userDataLoaded ? (
                <Skeleton height="22px" width="60px" />
              ) : (
                <Balance
                  fontSize="14px"
                  bold
                  color="text"
                  value={getBalanceAmount(totalLocked, tokens.xvvs.decimals).toNumber()}
                  decimals={2}
                  ml="6px"
                />
              )}
            </FixedHeightRow>
            <FixedHeightRow my="24px">
              <RowFixed>
                <TagComponent
                  variant="success"
                  title={`1 xVVS = ${formatNumber(parseFloat(rate?.toSignificant(18)), 0, 4) || ''} VVS`}
                />
              </RowFixed>
            </FixedHeightRow>
            <ActionButtons apr={apr} />
          </AutoColumn>
        </Card>
        <RecommendSection />
        <Box mt="24px">
          <StyledTextWithUrl
            color="textSubtle"
            textAlign="center"
            fontSize="16px"
            dangerouslySetInnerHTML={{
              __html: t('Don’t have any %token% yet? Get some from %urlOpenTag%%displayText%%urlCloseTag%', {
                token: 'VVS',
                urlOpenTag: `<a href="./swap?inputCurrency=${tokens.usdc.address}&outputCurrency=${tokens.vvs.address}">`,
                urlCloseTag: '</a>',
                displayText: t('VVS Bling Swap'),
              }),
            }}
          />
        </Box>
      </Body>
    </StyledCard>
  )
}

export default BarCard
